import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Trophy } from 'lucide-react';

import { PageHeader, PageShell } from '~/components/layout';
import {
  BarChartCard,
  DashboardCard,
  FailedSections,
  SECTION_FAILED,
  StatTile,
  StatTileSkeleton,
  StatsRow,
} from '~/components/dashboard';
import { CardSection, EmptyState, PanelTable, type PanelTableColumn } from '~/components/ui';
import { formatLocalTime } from '~/lib/datetime';
import { formatDuration, formatNumber, formatPercent, humanize, plural } from '~/lib/format';
import { capacitySeverity, successSeverity } from '~/lib/severity';
import { useGetUsageQuery, useGetUsageStatsQuery } from '~/modules/query/ai/settings';
import { useListPlacementsQuery } from '~/modules/query/ai/placements';
import type { UsageDailyPoint, UsageDimensionPoint } from '~/modules/query/ai/types';
import { getAnalyticsErrorInfo } from '~/lib/ai/analytics';
import { routes } from '~/app/routes';

const DAYS = 30;

const toDateKey = (date: Date) => date.toISOString().slice(0, 10);

/** One point per calendar day in the window, so a quiet day is a zero rather than a gap. */
function normalizeDaily(
  points: UsageDailyPoint[],
  startIso: string,
  endIso: string,
): UsageDailyPoint[] {
  const byDate = new Map(points.map((point) => [point.date, point]));
  const keys: string[] = [];
  const cursor = new Date(startIso);
  const end = new Date(endIso);

  cursor.setUTCHours(0, 0, 0, 0);
  end.setUTCHours(0, 0, 0, 0);

  while (cursor.getTime() <= end.getTime()) {
    keys.push(toDateKey(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return keys.map(
    (date) =>
      byDate.get(date) ?? {
        date,
        interactions: 0,
        promptTokens: 0,
        completionTokens: 0,
        totalTokens: 0,
        successCount: 0,
        errorCount: 0,
        avgDurationMs: 0,
      },
  );
}

const dimensionColumns = (header: string): PanelTableColumn<UsageDimensionPoint>[] => [
  { header, render: (row) => humanize(row.key) },
  { header: 'Tokens', render: (row) => formatNumber(row.totalTokens) },
  { header: 'Interactions', render: (row) => formatNumber(row.interactions) },
  { header: 'Success', render: (row) => formatPercent(row.successRate) },
  { header: 'Avg duration', render: (row) => formatDuration(row.avgDurationMs) },
];

/** The AI group's landing page: budgets, 30-day trends, and where the usage comes from (DESIGN §7). */
export default function Overview() {
  const usageRange = useMemo(() => {
    const endDate = new Date();
    const startDate = new Date(endDate);
    startDate.setDate(startDate.getDate() - (DAYS - 1));
    return { startDate: startDate.toISOString(), endDate: endDate.toISOString() };
  }, []);

  const usage = useGetUsageQuery();
  const stats = useGetUsageStatsQuery(usageRange);
  const placements = useListPlacementsQuery();

  const daily = useMemo(
    () => normalizeDaily(stats.data?.daily ?? [], usageRange.startDate, usageRange.endDate),
    [stats.data?.daily, usageRange],
  );
  const rows = daily.map((point) => ({
    label: formatLocalTime(point.date, { month: 'short', day: 'numeric' }),
    prompt: point.promptTokens,
    completion: point.completionTokens,
    interactions: point.interactions,
  }));

  const summary = stats.data?.summary;
  const topPlacements = stats.data?.topPlacements ?? [];
  const channels = stats.data?.channels ?? [];
  const models = stats.data?.models ?? [];
  const hasRequests = !!summary && summary.successCount + summary.failureCount > 0;
  const totalPlacements = placements.data?.placements.length ?? 0;
  const activePlacements =
    placements.data?.placements.filter((placement) => placement.status === 'active').length ?? 0;

  // §7.5: one banner naming the sections that failed, with one Retry; each failed card points to it.
  const failedSections = [
    usage.isError ? 'token usage' : null,
    stats.isError ? 'daily trends' : null,
    placements.isError ? 'placements' : null,
  ].filter(Boolean) as string[];
  const firstError = usage.error ?? stats.error ?? placements.error;
  const errorInfo = failedSections.length ? getAnalyticsErrorInfo(firstError) : null;
  const retry = () => {
    if (usage.isError) usage.refetch();
    if (stats.isError) stats.refetch();
    if (placements.isError) placements.refetch();
  };

  return (
    <PageShell>
      <PageHeader
        title="Overview"
        breadcrumbs={[{ label: 'AI' }, { label: 'Overview' }]}
        testId="ai-overview-page"
      />

      <FailedSections
        failed={failedSections}
        message={errorInfo?.isDbUnavailable ? errorInfo.message : undefined}
        onRetry={retry}
        testId="ai-overview"
      />

      <section aria-label="AI overview" className="grid grid-cols-12 gap-4 md:gap-6">
        <div className="col-span-12">
          <StatsRow ariaLabel="AI usage">
            {usage.isLoading ? (
              <>
                <StatTileSkeleton />
                <StatTileSkeleton />
              </>
            ) : (
              <>
                <StatTile
                  label="Daily token usage"
                  value={usage.data ? formatPercent(usage.data.daily.percentUsed) : undefined}
                  severity={usage.data ? capacitySeverity(usage.data.daily.percentUsed) : undefined}
                />
                <StatTile
                  label="Monthly token usage"
                  value={usage.data ? formatPercent(usage.data.monthly.percentUsed) : undefined}
                  severity={
                    usage.data ? capacitySeverity(usage.data.monthly.percentUsed) : undefined
                  }
                />
              </>
            )}
            {stats.isLoading ? (
              <StatTileSkeleton />
            ) : (
              <StatTile
                label="Success rate"
                value={summary ? formatPercent(summary.successRate) : undefined}
                severity={hasRequests ? successSeverity(summary.successRate) : undefined}
              />
            )}
            {placements.isLoading ? (
              <StatTileSkeleton />
            ) : (
              <StatTile
                label="Placements active"
                value={placements.data ? `${activePlacements} of ${totalPlacements}` : undefined}
              />
            )}
          </StatsRow>
        </div>

        <BarChartCard
          className="col-span-12 xl:col-span-6"
          title={`Daily token volume (${DAYS}d)`}
          description="Prompt and completion tokens per day."
          ariaLabel={`Prompt and completion tokens per day, last ${DAYS} days`}
          loading={stats.isLoading}
          error={stats.isError ? SECTION_FAILED : undefined}
          rows={rows}
          keys={[
            { id: 'prompt', label: 'Prompt' },
            { id: 'completion', label: 'Completion' },
          ]}
          summary={`${formatNumber(stats.data?.totalTokens ?? 0)} tokens in total: ${formatNumber(stats.data?.promptTokens ?? 0)} prompt and ${formatNumber(stats.data?.completionTokens ?? 0)} completion.`}
          emptyTitle="No token usage yet"
          emptyDescription="Tokens appear here once a placement or digital assistant runs a prompt."
        />

        <BarChartCard
          className="col-span-12 xl:col-span-6"
          title={`Daily interactions (${DAYS}d)`}
          description="How many prompts ran each day."
          ariaLabel={`AI interactions per day, last ${DAYS} days`}
          loading={stats.isLoading}
          error={stats.isError ? SECTION_FAILED : undefined}
          rows={rows}
          keys={[{ id: 'interactions', label: 'Interactions' }]}
          summary={`${plural(stats.data?.totalInteractions ?? 0, 'interaction')}, ${formatNumber(summary?.successCount ?? 0)} successful and ${formatNumber(summary?.failureCount ?? 0)} failed.`}
          emptyTitle="No interactions yet"
          emptyDescription="Each prompt a person sends is counted here."
        />

        <DashboardCard
          className="col-span-12 md:col-span-6"
          loading={stats.isLoading}
          error={stats.isError ? SECTION_FAILED : undefined}
          title="Highest usage placements"
          headerProps={{ description: 'Ranked by tokens over the last 30 days.' }}
        >
          {topPlacements.length > 0 ? (
            <ol className="custom-scrollbar max-h-96 space-y-2 overflow-y-auto pr-1">
              {topPlacements.map((placement, index) => (
                <li key={placement.placementKey}>
                  <Link
                    to={routes.aiPlacement(placement.placementKey)}
                    className="flex items-center justify-between gap-3 rounded-lg bg-gray-50 p-3 transition-colors hover:bg-gray-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 dark:bg-white/[0.03] dark:hover:bg-white/[0.06] dark:focus-visible:ring-offset-gray-900"
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-sm font-medium text-gray-800 dark:text-white/90">
                        #{index + 1} {placement.placementKey}
                      </span>
                      <span className="block text-xs text-gray-500 dark:text-gray-400">
                        {plural(placement.interactions, 'interaction')} •{' '}
                        {formatPercent(placement.successRate)} success •{' '}
                        {formatDuration(placement.avgDurationMs)} avg
                      </span>
                    </span>
                    <span className="shrink-0 text-base font-semibold text-gray-800 dark:text-white/90">
                      {formatNumber(placement.totalTokens)}
                    </span>
                  </Link>
                </li>
              ))}
            </ol>
          ) : (
            <EmptyState
              icon={<Trophy className="size-8" aria-hidden="true" />}
              title="No placement usage yet"
              description="Placements rank here once their prompts have run."
            />
          )}
        </DashboardCard>

        <DashboardCard
          className="col-span-12 md:col-span-6"
          loading={stats.isLoading}
          error={stats.isError ? SECTION_FAILED : undefined}
          title="Usage by channel and model"
          headerProps={{ description: 'Where the tokens went, over the last 30 days.' }}
        >
          <div className="space-y-6">
            <CardSection title="By channel">
              <PanelTable
                columns={dimensionColumns('Channel')}
                data={channels}
                getRowId={(row) => row.key}
                ariaLabel="Token usage by channel"
                emptyMessage="No channel telemetry yet."
                emptyDescription="Channels appear here once a placement has run in the last 30 days."
              />
            </CardSection>
            <CardSection title="By model">
              <PanelTable
                columns={dimensionColumns('Model')}
                data={models}
                getRowId={(row) => row.key}
                ariaLabel="Token usage by model"
                emptyMessage="No model telemetry yet."
                emptyDescription="Models appear here once a placement has run in the last 30 days."
              />
            </CardSection>
          </div>
        </DashboardCard>
      </section>
    </PageShell>
  );
}
